import { useState, useEffect, useRef, useMemo } from "react";
import styles from "../../../styles/__labchat.module.scss";
import { ArrowLeft, Upload } from "lucide-react";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import { TbLocationFilled } from "react-icons/tb";
import { BsChevronUp, BsChevronDown } from "react-icons/bs";
import AttachmentPreview from "../../../components/chat/AttachmentPreview";
import ChatFileCard, { FILE_BUBBLE_STYLE } from "../../../components/chat/ChatFileCard";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { APP_PATH } from "../../../api/backendPath";
import DefaultAvatar from "../../../assets/images/default_avatar.png";
import { formatCustom as tzCustom } from "../../../utils/datetime";
import useSupportChat from "../../../hooks/useSupportChat";
import { getTokenUserId } from "../../../utils/socketAuth";


const LabChat = ({ onLogout, onNavigateToDashboard }) => {
  const { t } = useTranslation();
  const axiosPrivateHook = useAxiosPrivate;
  // Support chat with the admin team: room resolution, history, live messages (this room
  // only), read receipts and token refresh live in the hook.
  const { messages, setMessages, chatRoomId, chatLoading, sendSocketMessage } =
    useSupportChat();
  const [newMessage, setNewMessage] = useState("");
  const [chatOpen, setChatOpen] = useState(true);
  const [fileUploading, setFileUploading] = useState(false);
  // Attachment shown in the in-page preview (AttachmentPreview) instead of a new tab.
  const [previewAttachment, setPreviewAttachment] = useState(null);
  // Images that failed to load (expired link…): shown as a file card instead.
  const [failedImages, setFailedImages] = useState({});
  const openAttachment = (url, msg) => {
    if (!url) return;
    setPreviewAttachment({
      url,
      kind: msg?.message_type,
      fileName: msg?.metadata?.file_name || msg?.message || "",
      contentType: msg?.metadata?.content_type || "",
      fileSize: msg?.metadata?.file_size,
    });
  };
  const chatMessagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  // Track blob preview URLs so we can revoke them on unmount (not while shown).
  const objectUrlsRef = useRef([]);
  useEffect(
    () => () => {
      objectUrlsRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (e) {
          /* no-op */
        }
      });
    },
    [],
  );

  const reqUserId = useMemo(() => getTokenUserId(), []);

  // Lab's own avatar (cached by the dashboard layout) for the sender bubbles.
  const selfAvatar = useMemo(
    () => sessionStorage.getItem("provider_avatar_url") || DefaultAvatar,
    [],
  );

  // Support team (admin) avatar: always the default image. Support is handled by
  // multiple admins/super admins (a provider's message broadcasts to all of them
  // and any can reply), so there is no single admin avatar to show.
  const isMe = (senderId) =>
    senderId != null && reqUserId != null && Number(senderId) === Number(reqUserId);
  const avatarForSender = (senderId) =>
    isMe(senderId) ? selfAvatar : DefaultAvatar;

  useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    const hasText = newMessage.trim().length > 0;
    if (!hasText) return;
    if (!chatRoomId) return;

    const msgText = newMessage.trim();
    setNewMessage("");
    setMessages((prev) => [
      ...prev,
      {
        id: `temp_${Date.now()}`,
        sender_id: reqUserId,
        message: msgText,
        message_type: "text",
        created_at: new Date().toISOString(),
      },
    ]);
    sendSocketMessage({
      room_id: chatRoomId,
      message: msgText,
      message_type: "text",
    });
  };

  // Upload + send a file immediately (no staged preview / confirmation step).
  const uploadAndSendFile = async (file) => {
    if (!file || !chatRoomId) return;
    const isImage = file.type.startsWith("image/");
    // Keep a local blob preview for the whole session so the image renders
    // instantly and never reloads (prevents the flicker / colored-box flash).
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    if (previewUrl) objectUrlsRef.current.push(previewUrl);
    const tempId = `temp_${Date.now()}`;

    setFileUploading(true);
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        sender_id: reqUserId,
        message: file.name,
        message_type: isImage ? "image" : "file",
        metadata: { file_name: file.name, previewUrl },
        created_at: new Date().toISOString(),
      },
    ]);

    try {
      const axiosPrivate = await axiosPrivateHook();
      const formData = new FormData();
      formData.append("file", file);
      const uploadResp = await axiosPrivate.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.consultationChatUpload}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const s3Key = uploadResp?.data?.data?.s3_key;
      const presignedUrl = uploadResp?.data?.data?.presigned_url;
      if (s3Key) {
        sendSocketMessage({
          room_id: chatRoomId,
          message: file.name,
          message_type: isImage ? "image" : "file",
          metadata: {
            s3_key: s3Key,
            file_name: file.name,
            file_size: file.size,
            content_type: file.type,
            presigned_url: presignedUrl,
          },
        });
      }
      // Update the SAME optimistic message in place (keep its id and blob preview
      // so the bubble doesn't remount or swap image source).
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? {
                ...m,
                metadata: {
                  ...m.metadata,
                  s3_key: s3Key,
                  presigned_url: presignedUrl,
                  file_size: file.size,
                  content_type: file.type,
                },
              }
            : m,
        ),
      );
    } catch (err) {
      console.error("File upload failed:", err);
    } finally {
      setFileUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    // Upload and send right away — no preview or confirmation.
    uploadAndSendFile(file);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return tzCustom(d, { hour: "2-digit", minute: "2-digit" });
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard?.();
    }
  };

  return (
    <LabDashboardLayout
      currentPage="lab_chat"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      {previewAttachment && (
        <AttachmentPreview
          url={previewAttachment.url}
          kind={previewAttachment.kind}
          fileName={previewAttachment.fileName}
          contentType={previewAttachment.contentType}
          fileSize={previewAttachment.fileSize}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
      <div>
        <div className={styles.innerWrapper}>
          <h1 className={styles.settingsTitle}>
            {t("lab.chat.title", "Chat")}
          </h1>
          <div className={styles["settings-card"]}>
            <div className={styles.chatContainer}>
              <div className={styles.chatHeader}>
                <div className={styles.chatHeaderLeft}>
                  <button className={styles.iconButton}>
                    <ArrowLeft size={20} />
                  </button>
                  <h6 className={styles.chatTitle}>
                    {t("lab.chat.withAdmin", "Chat with Admin")}
                  </h6>
                </div>
                <button
                  className={styles.iconButton}
                  onClick={() => setChatOpen(!chatOpen)}
                >
                  {chatOpen ? (
                    <BsChevronUp size={20} />
                  ) : (
                    <BsChevronDown size={20} />
                  )}
                </button>
              </div>

              {chatOpen && (
                <>
                  <div className={styles.chatMessages}>
                    {chatLoading ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#999",
                        }}
                      >
                        {t("lab.chat.loadingMessages", "Loading messages...")}
                      </div>
                    ) : messages.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#999",
                        }}
                      >
                        {t(
                          "lab.chat.emptySupport",
                          "No messages yet. Start the conversation with the support team.",
                        )}
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isMine = isMe(msg.sender_id);
                        const isFile =
                          msg.message_type === "file" ||
                          msg.message_type === "image";
                        const fileUrl =
                          msg.metadata?.previewUrl ||
                          msg.metadata?.presigned_url;
                        const msgAvatar = avatarForSender(msg.sender_id);
                        return (
                          <div
                            key={msg.id}
                            className={`${styles.messageWrapper} ${isMine ? styles.userTxt : styles.adminTxt}`}
                          >
                            {!isMine && (
                              <div className={styles.userImgWrap}>
                                <img
                                  src={msgAvatar}
                                  alt="Support"
                                  className={styles.userImg}
                                  onError={(e) => {
                                    e.currentTarget.src = DefaultAvatar;
                                  }}
                                />
                              </div>
                            )}
                            <div>
                              <div className={styles.messageTime}>
                                {formatTime(msg.created_at)}
                              </div>
                              <div
                                className={`${styles.messageBubble} ${isMine ? styles.user : styles.admin}`}
                                style={isFile && !(msg.message_type === "image" && fileUrl && !failedImages[msg.id]) ? FILE_BUBBLE_STYLE : undefined}
                              >
                                {isFile &&
                                msg.message_type === "image" &&
                                fileUrl &&
                                !failedImages[msg.id] ? (
                                  <img
                                    src={fileUrl}
                                    alt={msg.metadata?.file_name || msg.message}
                                    title={t("chat.preview.clickToView", "Click to view")}
                                    style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, cursor: "pointer", display: "block" }}
                                    onClick={() => openAttachment(fileUrl, msg)}
                                    onError={() => setFailedImages((prev) => ({ ...prev, [msg.id]: true }))}
                                  />
                                ) : isFile ? (
                                  <ChatFileCard
                                    fileName={msg.metadata?.file_name || msg.message}
                                    contentType={msg.metadata?.content_type}
                                    fileSize={msg.metadata?.file_size}
                                    mine={isMine}
                                    disabled={!fileUrl}
                                    onOpen={() => openAttachment(fileUrl, msg)}
                                  />
                                ) : (
                                  <p className={styles.messageText}>
                                    {msg.message}
                                  </p>
                                )}
                              </div>
                            </div>
                            {isMine && (
                              <div className={styles.userImgWrap}>
                                <img
                                  src={msgAvatar}
                                  alt="Me"
                                  className={styles.userImg}
                                  onError={(e) => {
                                    e.currentTarget.src = DefaultAvatar;
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    <div ref={chatMessagesEndRef} />
                  </div>

                  <div className={styles.chatFooter}>
                    <button
                      className={styles.iconButton}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={fileUploading}
                    >
                      <Upload size={16} />{" "}
                      {t("lab.chat.uploadDocument", "Upload Document")}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                      accept="image/*,.pdf,.doc,.docx"
                    />
                    <div className={styles.inputWrapper}>
                      <input
                        type="text"
                        className={styles.inputField}
                        placeholder={t("chat.typeMessage", "Type a message")}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") handleSendMessage();
                        }}
                        disabled={fileUploading}
                      />
                      <button
                        className={styles.sendButton}
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || fileUploading}
                      >
                        <TbLocationFilled size={20} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </LabDashboardLayout>
  );
};

export default LabChat;
